import { Formik, Form } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "react-bootstrap"
import { useTranslation } from "react-i18next";
import TextBox from "../../../../controls/form/TextBox";
import TextArea from "../../../../controls/form/TextArea";
import Dropdown from "../../../../controls/form/Dropdown";
import GenericCheckBox from '../../../../controls/GenericCheckbox';
import Forms from "../../../../../services/forms";
import * as Yup from "yup";
import { useFormsConfig } from "../../Context";

const nameRegex = /^[a-zA-Z0-9_]+[a-zA-Z0-9]$/;

const CreateFormTemplate = ({
    show,
    onCreate,
    onClose
}) => {
    const { id: deploymentId, } = useParams();
    const { preferences } = useFormsConfig();
    const [translate] = useTranslation();

    const [selectTemplates, setSelectTemplates] = useState([]);
    const [selectVersions, setSelectVersions] = useState([]);
    const [showSaveButton, setShowSaveButton] = useState(false);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    const getSelectTemplates = useCallback(async () => {
        const templates = await Forms.getTemplates({ deploymentId });
        if (!templates.error && templates.data) {
            setSelectTemplates(templates.data.filter(template => template.isPublished));
        }
    }, [deploymentId, setSelectTemplates]);

    const getSelectVersions = useCallback(async (name) => {
        setSelectVersions([]);
        const versions = await Forms.getTemplateVersions({ deploymentId, name });
        if (!versions.error && versions.data) {
            setSelectVersions(versions.data.slice().reverse().map(version => ({
                _id: version._id,
                version: version.version,
                display: `${version.version} - ${translate(version.isActive ? "OTM_ACTIVE" : (version.isPublished ? "OTM_PUBLISHED" : "OTM_IN_PROGRESS"))}`
            })));
        }
    }, [deploymentId, setSelectVersions, translate]);

    const dropdownTemplates = useMemo(() => {
        if (!selectTemplates || selectTemplates.length === 0) {
            return [];
        }

        const templateGroupingIds = [];
        selectTemplates.forEach(template => { templateGroupingIds.push(template.templateGroupingId); });

        const uniqueGroupingIds = Array.from(new Set(templateGroupingIds));
        return uniqueGroupingIds.map((id) => ({ _id: id, name: selectTemplates.find(template => template.templateGroupingId === id).name }));
    }, [selectTemplates]);

    useEffect(() => {
        if (deploymentId) {
            getSelectTemplates();
        }
    }, [deploymentId, getSelectTemplates]);

    const addFormTemplate = useCallback(async (values) => {
        const response = await Forms.createTemplate({
            deploymentId,
            body: values
        });

        if (!response.error) {
            onCreate(response.data);
        }
    }, [deploymentId, onCreate]);

    const renderForm = useMemo(() => {
        return <Formik initialValues={{
            name: "",
            description: "",
            formType: preferences.formType ?? "plain",
            submitHiddenFields: preferences.submitHiddenFields ?? false,
            copyName: "",
            copyId: ""
        }} onSubmit={addFormTemplate}
            validateOnMount
            validationSchema={Yup.object({
                name: Yup.string().matches(nameRegex, translate("OTM_INVALID_FORM_NAME")).required(translate("OTM_FORM_TEMPLATE_NAME_REQUIRED")),
                copyId: Yup.string().nullable().when(['copyName'], (copyName) => {
                    if (copyName && copyName !== "--Select--") {
                        return Yup.string().required(translate("OTM_COPY_VERSION_REQUIRED"));
                    }
                    else {
                        return Yup.string();
                    }
                })
            })}
            enableReinitialize>
            {props => (<Form onChange={handleOnChange} className="flex-grow-1 d-flex flex-column">
                <Modal.Header>
                    <label className="h5">{translate('OTM_ADD_FORM_TEMPLATE')}</label>
                </Modal.Header>

                <Modal.Body>
                    <div className="row mt-n3 align-items-start">
                        <div className='col-12 col-lg-6 mt-3'>
                            <Dropdown
                                heading={translate("OTM_COPY_EXISTING_TEMPLATE")}
                                valueKey="name"
                                displayValueKey="name"
                                options={dropdownTemplates}
                                grow={true}
                                addEmptyOption={true}
                                showSearchBar={true}
                                onSelect={(value) => { props.setFieldValue('copyId', ''); getSelectVersions(value); handleOnChange(); }}
                                name="copyName"
                            />
                        </div>
                        <div className="col-12 col-lg-6 mt-3">
                            <Dropdown
                                heading={translate("OTM_COPY_EXISTING_VERSION")}
                                valueKey="_id"
                                displayValueKey="display"
                                options={selectVersions}
                                grow={true}
                                onSelect={handleOnChange}
                                addEmptyOption={true}
                                showSearchBar={true}
                                name="copyId"
                            />
                        </div>
                        <div className='col-12 mt-3'>
                            <TextBox label={translate('OTM_FORM_TEMPLATE_NAME')} isRequired={true} type='text' name='name' />
                        </div>
                        <div className='col-12 mt-3'>
                            <TextArea label={translate('OTM_DESCRIPTION')} type='text' name='description' />
                        </div>
                        <div className='col-12 col-lg-6 mt-3'>
                            <GenericCheckBox
                                tooltip={translate("OTM_FORM_TYPE_TOOLTIP")}
                                buttonTooltip={true}
                                initialValue={props.values.formType === 'banner'}
                                label={translate('OTM_FORM_TYPE')}
                                name='formType'
                                onChange={(value) => { props.setFieldValue('formType', props.values['formType'] === 'banner' ? 'plain' : 'banner'); }}
                            />
                        </div>
                        <div className='col-12 col-lg-6 mt-3'>
                            <GenericCheckBox
                                tooltip={translate("OTM_SUBMIT_HIDDEN_FIELDS_TOOLTIP")}
                                buttonTooltip={true}
                                initialValue={props.values.submitHiddenFields}
                                label={translate('OTM_SUBMIT_HIDDEN_FIELDS')}
                                name='submitHiddenFields'
                                onChange={(value) => { props.setFieldValue('submitHiddenFields', value); }}
                            />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    {showSaveButton && props.isValid && <Button size='sm' variant='primary' type='submit'>{translate('OTM_SAVE')} </Button>}
                    <Button size='sm' variant='secondary' onClick={onClose}>{translate('OTM_CANCEL')} </Button>
                </Modal.Footer>
            </Form>)}
        </Formik >
    }, [onClose, addFormTemplate, translate, dropdownTemplates, preferences, getSelectVersions, selectVersions, handleOnChange, showSaveButton])

    return <Modal
        size={'lg'}
        show={show}
        backdrop={'static'}>
        {renderForm}
    </Modal>
}

export default CreateFormTemplate;